/**
 * @docs docs/features/website-navigation-shell
 */

import React from 'react';

import useContext from '@/client/context';
import RoundLogo from '@/client/assets/identity/logo64.png';
// The 480px cut, not the 1370px master: the slot is 190px wide at most, and the build serves
// the largest generated size as `src`, so the master shipped 1300px for every page view.
import SignatureLockup from '@/client/assets/identity/signature-lockup-ink-480.png';
import CtaPill from '@uniquedomains/components/website/WebsiteCta';
import Icon from '@/client/components/ui/icon/Icon';
import MetricsComponent from '@/client/services/metrics/Component';
import useProductSession from '@/client/hooks/useProductSession';
import {
    buildLandingHeaderNavItems,
    isLandingHeaderNavItemCurrent,
    type LandingHeaderDomainsNavigation,
    type LandingHeaderNavItem,
} from '@/client/catalogs/landing/pageContent';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import { setLandingBodyClass, setLandingDocumentScrollLocked } from '@/client/pages/landing/lib/documentOverlayState';
import DomainsMegaMenu from './DomainsMegaMenu';
import MarketMegaMenu from './MarketMegaMenu';
import MobileActionBar, { type MobileActionBarAction } from './MobileActionBar';
import MobileSiteMenu from './MobileSiteMenu';
import type { LandingNavMenu, LandingNavSurface, MenuNavigateHandler } from './navClick';
import { resolveHeaderScrolledState } from './headerScrollState';
import ToolsMegaMenu from './ToolsMegaMenu';
import { resolveProductOrigin } from '@/client/services/conversion/product/productOrigin';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';
import { START_WITH_BRIEF_LABEL } from '@/client/catalogs/landing/accessOffer';
import type { Links } from '../../types';

const HEADER_MEGA_MENU_CLOSE_DELAY_MS = 400;
// Not "Start Free": the free plan is retired (ADR-0083); a signup opens the brief, then the decision.
const HEADER_PRIMARY_CTA_LABEL = START_WITH_BRIEF_LABEL;
const HEADER_OPEN_APP_LABEL = 'Open app';
const HEADER_NAV_ITEM_BASE_CLASS =
    'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition';
const HEADER_NAV_ITEM_HOVER_CLASS = 'hover:bg-slate-100 hover:text-slate-900';
const HEADER_NAV_ITEM_ACTIVE_CLASS = 'bg-slate-100 text-slate-950';
// `relative` is load bearing: a narrow panel rendered inside the group centres on its own
// trigger instead of on the whole bar, which is where a reader expects it to open.
const HEADER_MEGA_NAV_GROUP_BASE_CLASS =
    'group relative inline-flex items-stretch rounded-full text-sm font-medium transition';
const HEADER_MEGA_NAV_LINK_CLASS =
    'inline-flex cursor-pointer items-center rounded-full px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-900/20';
const EMPTY_DOMAINS_NAVIGATION: LandingHeaderDomainsNavigation = {
    tlds: [],
    sectors: [],
};
/**
 * Login left the desktop bar on 2026-09-17. These ids did not: the phone menu's Log in
 * row and the footer's Log in link both fire the one that matches the page, told apart
 * in analytics by `section`. The trigger catalog is a cross-app contract, so a surface
 * moving is not a reason to mint a second set of ids.
 */
export const HEADER_LOGIN_TRIGGER_BY_PAGE: Record<string, ConversionTriggerId> = {
    api_docs: 'website_api_header_login',
    domains_public: 'website_domains_header_login',
    landing_home: 'website_landing_header_login',
    pricing: 'website_pricing_header_login',
    product_appraise: 'website_product_appraise_header_login',
    product_radars: 'website_product_radars_header_login',
    product_search: 'website_product_search_header_login',
    product_verify: 'website_product_verify_header_login',
    product_watchlists: 'website_product_watchlists_header_login',
    solution_founders: 'website_solution_founders_header_login',
    solution_investors: 'website_solution_investors_header_login',
    solution_studios: 'website_solution_studios_header_login',
    team: 'website_team_header_login',
};
const HEADER_PRIMARY_TRIGGER_BY_PAGE: Record<string, ConversionTriggerId> = {
    api_docs: 'website_api_header_try_free',
    domains_public: 'website_domains_header_try_free',
    landing_home: 'website_landing_header_try_free',
    pricing: 'website_pricing_header_try_free',
    product_appraise: 'website_product_appraise_header_try_free',
    product_radars: 'website_product_radars_header_try_free',
    product_search: 'website_product_search_header_try_free',
    product_verify: 'website_product_verify_header_try_free',
    product_watchlists: 'website_product_watchlists_header_try_free',
    solution_founders: 'website_solution_founders_header_try_free',
    solution_investors: 'website_solution_investors_header_try_free',
    solution_studios: 'website_solution_studios_header_try_free',
    team: 'website_team_header_try_free',
};

type HeaderMenuId = 'domains' | 'market' | 'tools';
type HeaderMegaNavItem = Extract<LandingHeaderNavItem, { kind: 'mega' }>;
type HeaderProps = {
    links: Links;
    navigation?: LandingHeaderDomainsNavigation;
    brandHref?: string;
    currentPath?: string;
    sticky?: boolean;
    trackingPage: string;
    /**
     * The phone action bar. Omitted: Log in beside Start with my brief, the site default.
     * A page built around another action passes its own slots; `null` removes the
     * bar on pages that have no action to offer, such as the legal documents.
     */
    mobileActions?: HeaderMobileActions | null;
};

export type HeaderMobileActions = {
    secondary?: MobileActionBarAction | null;
    primary?: MobileActionBarAction;
};

const HEADER_MEGA_MENU_IDS: Record<HeaderMenuId, string> = {
    domains: 'landing-header-domains-menu',
    market: 'landing-header-market-menu',
    tools: 'landing-header-tools-menu',
};
const getHeaderMenuId = (menu: HeaderMenuId): string => HEADER_MEGA_MENU_IDS[menu];

export const resolveHeaderTriggerId = (
    trackingPage: string,
    map: Record<string, ConversionTriggerId>,
    fallback: ConversionTriggerId,
): ConversionTriggerId => map[trackingPage] || fallback;

const scrollWindowToTop = () => {
    if (typeof window === 'undefined') return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
};

export function Header({
    links,
    navigation = EMPTY_DOMAINS_NAVIGATION,
    brandHref = '/',
    currentPath = '',
    sticky = true,
    trackingPage,
    mobileActions,
}: HeaderProps) {
    // Every landing page funnels its resolved navigation payload through this
    // header, so this is the one seam that seeds the public read cache; page
    // `data` fetchers then reuse it instead of re-asking Product per render.
    // The empty fallback is a render placeholder, never a payload to cache.
    if (navigation !== EMPTY_DOMAINS_NAVIGATION) cachedLandingHeaderNavigation.seed(navigation);
    const { enforceDomainPublicationLinks, publicOrigins, renderedDomainResourcePaths } = useContext();
    /*
     * The one place the public site learns whether the visitor is already a member.
     * SSR cannot: these pages are rendered once and replayed to everyone, and the Product
     * session cookie is host-only on the Product host, so it never reaches this origin.
     * The header renders on every landing page, so probing here covers the whole site for
     * one request per document, shared with the metrics component and the /domains page.
     * See docs/fixes/2026-09-14-website-header-blind-to-members.md.
     */
    const { status: productSessionStatus } = useProductSession(publicOrigins.productOrigin);
    const isMember = productSessionStatus === 'member';
    const productHomeHref = `${resolveProductOrigin(publicOrigins.productOrigin)}/`;
    const headerRef = React.useRef<HTMLElement | null>(null);
    const headerNavRef = React.useRef<HTMLDivElement | null>(null);
    const headerMenuCloseTimeoutRef = React.useRef<number | null>(null);
    const pendingPointerMenuIdRef = React.useRef<string | null>(null);
    const suppressHeaderFocusOpenRef = React.useRef(false);
    const mobileMenuTriggerRef = React.useRef<HTMLButtonElement | null>(null);

    const [activeHeaderMenuId, setActiveHeaderMenuId] = React.useState<string | null>(null);
    const [isHeaderScrolled, setIsHeaderScrolled] = React.useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

    const publishedDomainPaths = enforceDomainPublicationLinks
        ? renderedDomainResourcePaths
        : undefined;
    const headerNavItems = React.useMemo(
        () => buildLandingHeaderNavItems(navigation, publishedDomainPaths),
        [navigation, publishedDomainPaths],
    );

    const activeHeaderMenu = React.useMemo(() => {
        const menu = headerNavItems.find((item) => item.id === activeHeaderMenuId);
        if (!menu || menu.kind !== 'mega') return null;
        return menu;
    }, [activeHeaderMenuId, headerNavItems]);

    const domainsMenu = React.useMemo(() => {
        const menu = headerNavItems.find((item) => item.id === 'domains');
        if (!menu || menu.kind !== 'mega' || menu.menu !== 'domains') return null;
        return menu;
    }, [headerNavItems]);

    const cancelHeaderMenuClose = React.useCallback(() => {
        if (headerMenuCloseTimeoutRef.current === null) return;

        window.clearTimeout(headerMenuCloseTimeoutRef.current);
        headerMenuCloseTimeoutRef.current = null;
    }, []);

    const closeMobileMenu = React.useCallback(() => setIsMobileMenuOpen(false), []);

    const scheduleHeaderMenuClose = React.useCallback(() => {
        cancelHeaderMenuClose();

        headerMenuCloseTimeoutRef.current = window.setTimeout(() => {
            setActiveHeaderMenuId(null);
            headerMenuCloseTimeoutRef.current = null;
        }, HEADER_MEGA_MENU_CLOSE_DELAY_MS);
    }, [cancelHeaderMenuClose]);

    const handleHeaderNavEnter = React.useCallback(
        (item: LandingHeaderNavItem) => {
            cancelHeaderMenuClose();

            if (item.kind !== 'mega') {
                setActiveHeaderMenuId(null);
                return;
            }

            setActiveHeaderMenuId(item.id);
        },
        [cancelHeaderMenuClose],
    );

    const handleHeaderNavFocus = React.useCallback(
        (item: HeaderMegaNavItem) => {
            if (suppressHeaderFocusOpenRef.current) {
                suppressHeaderFocusOpenRef.current = false;
                return;
            }

            handleHeaderNavEnter(item);
        },
        [handleHeaderNavEnter],
    );

    const focusFirstHeaderMenuItem = React.useCallback(
        (item: HeaderMegaNavItem) => {
            handleHeaderNavEnter(item);

            window.requestAnimationFrame(() => {
                document
                    .getElementById(getHeaderMenuId(item.menu))
                    ?.querySelector<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
                    ?.focus();
            });
        },
        [handleHeaderNavEnter],
    );

    const handleHeaderMegaNavKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>, item: HeaderMegaNavItem) => {
            if (event.key !== 'ArrowDown') return;

            event.preventDefault();
            focusFirstHeaderMenuItem(item);
        },
        [focusFirstHeaderMenuItem],
    );

    const handleHeaderMegaNavPointerDown = React.useCallback(
        (event: React.PointerEvent<HTMLElement>, item: HeaderMegaNavItem) => {
            pendingPointerMenuIdRef.current =
                event.pointerType !== 'mouse' && activeHeaderMenuId !== item.id ? item.id : null;
        },
        [activeHeaderMenuId],
    );

    const closeHeaderMenu = React.useCallback(() => {
        cancelHeaderMenuClose();
        setActiveHeaderMenuId(null);
    }, [cancelHeaderMenuClose]);

    /*
     * One event for every followed link in the bar, the three panels and the phone menu,
     * so a menu's use can be read without joining per-surface ids. It is not a conversion
     * trigger: the trigger catalog is a cross-app contract of conversion-path clicks, and a
     * menu link is not one. CTAs that open the product keep `landing_cta_click` and their
     * trigger ids; this event records only where the visitor went next.
     */
    const trackNavClick = React.useCallback(
        (menu: LandingNavMenu, surface: LandingNavSurface, item: string, href: string) => {
            track('landing_nav_click', { page: trackingPage, menu, item, href, surface });
        },
        [trackingPage],
    );

    const navigateFromMegaMenu = React.useCallback(
        (menu: HeaderMenuId): MenuNavigateHandler => (item, href) => trackNavClick(menu, 'megamenu', item, href),
        [trackNavClick],
    );

    const navigateFromMobileMenu = React.useCallback<MenuNavigateHandler>(
        (item, href) => {
            trackNavClick('mobile', 'mobile_menu', item, href);
            closeMobileMenu();
        },
        [closeMobileMenu, trackNavClick],
    );

    const handleHeaderMegaNavClick = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>, item: HeaderMegaNavItem) => {
            if (pendingPointerMenuIdRef.current === item.id) {
                pendingPointerMenuIdRef.current = null;
                event.preventDefault();
                handleHeaderNavEnter(item);
                return;
            }

            // Only a click that leaves for the hub page is navigation. The touch tap above
            // opens the panel instead, and counting it would inflate Domains with menu opens.
            if (item.href) trackNavClick('bar', 'header', item.id, item.href);
            closeHeaderMenu();
        },
        [closeHeaderMenu, handleHeaderNavEnter, trackNavClick],
    );

    /*
     * Tools has no hub page, so its trigger is a disclosure button rather than an
     * anchor: there is no URL to send a crawler or a keyboard user to, and `href="#"`
     * would be both. A click toggles the panel it controls, except on the tap that
     * opened it: a touch tap emits an emulated `mouseenter` before its `click`, so the
     * open and the toggle would land in the same gesture and the panel would flash shut.
     * The pointer-intent ref is the same one the anchor triggers use.
     */
    const handleHeaderMegaNavToggle = React.useCallback(
        (item: HeaderMegaNavItem) => {
            if (pendingPointerMenuIdRef.current === item.id) {
                pendingPointerMenuIdRef.current = null;
                handleHeaderNavEnter(item);
                return;
            }

            if (activeHeaderMenuId === item.id) {
                closeHeaderMenu();
                return;
            }

            handleHeaderNavEnter(item);
        },
        [activeHeaderMenuId, closeHeaderMenu, handleHeaderNavEnter],
    );

    // Where a header-owned click came from. The phone action bar reuses the header's
    // per-page trigger ids (the trigger catalog is a cross-app contract) and is told
    // apart by this section value instead of a second set of ids.
    const resolveCtaSection = React.useCallback(
        (surface: 'header' | 'action_bar') =>
            surface === 'action_bar' ? 'mobile_action_bar' : isMobileMenuOpen ? 'header_mobile_menu' : 'header',
        [isMobileMenuOpen],
    );

    const triggerPrimaryCta = React.useCallback((surface: 'header' | 'action_bar' = 'header') => {
        const section = resolveCtaSection(surface);

        trackLandingCta({
            page: trackingPage,
            section,
            label: HEADER_PRIMARY_CTA_LABEL,
            kind: 'product',
            href: links.productBaseUrl,
            surface: 'header',
        });
        trackConversionTrigger(
            resolveHeaderTriggerId(trackingPage, HEADER_PRIMARY_TRIGGER_BY_PAGE, 'website_landing_header_try_free'),
            {
                href: links.productBaseUrl,
                page: trackingPage,
                section,
            },
        );

        track('cta_open_screener_click', {
            section,
            label: HEADER_PRIMARY_CTA_LABEL.toLowerCase(),
        });

        closeMobileMenu();
    }, [closeMobileMenu, links.productBaseUrl, resolveCtaSection, trackingPage]);

    /*
     * A member's click is navigation, not conversion: no trigger id is emitted. The
     * conversion-trigger maps above are per-page catalogs of signup and login intents,
     * pinned by product-search-page-contract.test.cjs, and there is no entry for a member
     * who is already past the funnel. `landing_cta_click` still records the click.
     */
    const triggerOpenAppCta = React.useCallback((surface: 'header' | 'action_bar' = 'header') => {
        trackLandingCta({
            page: trackingPage,
            section: resolveCtaSection(surface),
            label: HEADER_OPEN_APP_LABEL,
            kind: 'product',
            href: productHomeHref,
            surface: 'header',
        });

        closeMobileMenu();
    }, [closeMobileMenu, productHomeHref, resolveCtaSection, trackingPage]);

    const triggerLoginCta = React.useCallback((surface: 'header' | 'action_bar' = 'header') => {
        const section = resolveCtaSection(surface);

        trackLandingCta({
            page: trackingPage,
            section,
            label: 'Login',
            kind: 'auth',
            href: links.signIn,
            // Login is a phone-only control in the shell since 2026-09-17: it lives in the
            // phone menu and in the action bar, so the surface records which of the two.
            surface: surface === 'action_bar' ? 'action_bar' : 'mobile_menu',
        });
        trackConversionTrigger(
            resolveHeaderTriggerId(trackingPage, HEADER_LOGIN_TRIGGER_BY_PAGE, 'website_landing_header_login'),
            {
                href: links.signIn,
                page: trackingPage,
                section,
            },
        );

        closeMobileMenu();
    }, [closeMobileMenu, links.signIn, resolveCtaSection, trackingPage]);

    const trackGithubNavOpen = React.useCallback(
        (section: string) => {
            trackLandingCta({
                page: trackingPage,
                section,
                label: 'GitHub continuity',
                kind: 'github',
                href: links.githubLists,
                surface: 'header',
            });
            track('github_repo_open_attempt', {
                section,
                target: 'github_lists',
            });
        },
        [links.githubLists, trackingPage],
    );

    const handleBrandClick = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            if (typeof window === 'undefined') return;
            if (window.location.pathname !== brandHref) return;

            event.preventDefault();
            setActiveHeaderMenuId(null);

            if (isMobileMenuOpen) {
                closeMobileMenu();
                window.setTimeout(() => scrollWindowToTop(), 20);
                return;
            }

            scrollWindowToTop();
        },
        [brandHref, closeMobileMenu, isMobileMenuOpen],
    );

    React.useEffect(() => {
        const headerElement = headerRef.current;
        if (!headerElement) return;

        const updateTopbarHeight = () => {
            const height = headerElement.getBoundingClientRect().height;
            const topOffset = Number.parseFloat(window.getComputedStyle(headerElement).top || '0') || 0;
            document.documentElement.style.setProperty('--ud-topbar-h', `${Math.ceil(height + topOffset)}px`);
        };

        updateTopbarHeight();

        if (typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver(() => updateTopbarHeight());
        observer.observe(headerElement);

        return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;

        const updateScrolledState = () => {
            setIsHeaderScrolled((wasScrolled) => resolveHeaderScrolledState(wasScrolled, window.scrollY));
        };

        updateScrolledState();
        window.addEventListener('scroll', updateScrolledState, { passive: true });

        return () => window.removeEventListener('scroll', updateScrolledState);
    }, [currentPath]);

    React.useEffect(() => {
        if (!activeHeaderMenuId) return;

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (headerNavRef.current?.contains(target)) return;
            setActiveHeaderMenuId(null);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;

            const activeTrigger = headerNavRef.current?.querySelector<HTMLElement>(
                `[data-testid="header-${activeHeaderMenuId}-link"]`,
            );

            // Focus first, close second. Closing schedules a render that unmounts the panel,
            // and Preact can run it before this handler reaches the focus call, which leaves
            // focus on a removed node and strands keyboard users at the top of the document.
            // Moving the focus ahead of the state change makes it land while the trigger is
            // certainly still mounted. The suppression ref keeps that focus from reopening
            // the menu it is closing.
            if (activeTrigger && document.activeElement !== activeTrigger) {
                suppressHeaderFocusOpenRef.current = true;
                activeTrigger.focus();
            }

            setActiveHeaderMenuId(null);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeHeaderMenuId]);

    React.useEffect(() => {
        const overlayClassName = 'ud-landing-header-menu-open';
        const scrollLockId = 'landing-header-menu';

        setLandingBodyClass(overlayClassName, isMobileMenuOpen);
        setLandingDocumentScrollLocked(scrollLockId, isMobileMenuOpen);

        return () => {
            setLandingBodyClass(overlayClassName, false);
            setLandingDocumentScrollLocked(scrollLockId, false);
        };
    }, [isMobileMenuOpen]);

    React.useEffect(() => {
        if (!isMobileMenuOpen) return;
        return () => mobileMenuTriggerRef.current?.focus();
    }, [isMobileMenuOpen]);

    React.useEffect(() => {
        return () => {
            if (headerMenuCloseTimeoutRef.current === null) return;
            window.clearTimeout(headerMenuCloseTimeoutRef.current);
        };
    }, []);

    // Site default for the phone action bar. A member has no Log in slot, and their
    // primary action opens the app instead of starting a signup.
    const defaultPrimaryAction: MobileActionBarAction = isMember
        ? { label: HEADER_OPEN_APP_LABEL, href: productHomeHref, onClick: () => triggerOpenAppCta('action_bar') }
        : {
              label: HEADER_PRIMARY_CTA_LABEL,
              href: links.productBaseUrl,
              onClick: () => triggerPrimaryCta('action_bar'),
              testId: 'mobile-action-bar-primary',
          };
    const defaultSecondaryAction: MobileActionBarAction | null = isMember
        ? null
        : { label: 'Log in', href: links.signIn, onClick: () => triggerLoginCta('action_bar') };

    return (
        <>
            {activeHeaderMenu ? (
                <div
                    aria-hidden="true"
                    class="ud-landing-header-menu-backdrop pointer-events-none fixed inset-0 z-30 hidden bg-[#f8f6f1]/35 backdrop-blur-md lg:block"
                />
            ) : null}

            <header
                ref={headerRef}
                class={[
                    sticky ? 'sticky top-0' : 'relative',
                    'z-30 transition-[padding-top] duration-300 lg:z-40 lg:flex lg:justify-center lg:px-6',
                    // The floating pill drops from 40px to 10px once the page scrolls. The phone
                    // bar is flush with the top edge, so the offset is desktop only.
                    isHeaderScrolled ? 'lg:pt-2.5' : 'lg:pt-10',
                ].join(' ')}
            >
                <MetricsComponent />
                {/*
                  * One bar, two shapes. On a phone it is a full-width cream strip holding the
                  * lockup and the menu button, so the status bar and the page share one ground.
                  * From `lg` up it becomes the floating white pill with the navigation inside.
                  */}
                <div
                    class={[
                        'ud-landing-header-bar flex items-center justify-between gap-4 border-b border-[rgba(27,29,30,0.06)] bg-[rgba(248,246,241,0.94)] px-4 py-2.5 backdrop-blur-md',
                        'lg:w-fit lg:max-w-[calc(100%-3rem)] lg:justify-start lg:rounded-full lg:border lg:border-[#ffffffe6] lg:bg-[#fffffff0] lg:py-2.5 lg:pl-5 lg:pr-2 lg:backdrop-blur-none lg:transition-[box-shadow] lg:duration-300',
                        isHeaderScrolled
                            ? 'lg:shadow-[0_18px_50px_rgba(27,29,30,0.14)]'
                            : 'lg:shadow-[0_14px_40px_rgba(27,29,30,0.1),0_1px_2px_rgba(27,29,30,0.05)]',
                    ].join(' ')}
                >
                    <a
                        href={brandHref}
                        onClick={handleBrandClick}
                        class={[
                            'relative flex h-11 w-[158px] shrink-0 items-center overflow-hidden motion-reduce:transition-none lg:h-9 lg:transition-[width] lg:duration-300',
                            // Only the desktop pill collapses to the round mark on scroll.
                            isHeaderScrolled ? 'lg:w-9' : 'lg:w-[190px]',
                        ].join(' ')}
                        aria-label="Unique Domains home"
                    >
                        <img
                            src={SignatureLockup}
                            alt=""
                            width="190"
                            height="36"
                            class={[
                                'absolute left-0 h-[30px] w-auto max-w-none origin-left transition-[opacity,transform] duration-200 motion-reduce:transition-none lg:h-9',
                                isHeaderScrolled ? 'lg:scale-95 lg:opacity-0' : '',
                            ].join(' ')}
                        />
                        <img
                            src={RoundLogo}
                            alt=""
                            width="36"
                            height="36"
                            class={[
                                'absolute left-0 hidden h-9 w-9 transition-[opacity,transform] duration-200 motion-reduce:transition-none lg:block',
                                isHeaderScrolled ? 'scale-100 opacity-100' : 'scale-90 opacity-0',
                            ].join(' ')}
                        />
                    </a>

                    <div class="hidden justify-center px-2 lg:flex">
                        <div
                            ref={headerNavRef}
                            class="relative"
                            onMouseEnter={cancelHeaderMenuClose}
                            onMouseLeave={scheduleHeaderMenuClose}
                        >
                            <nav aria-label="Primary" class="items-center gap-px lg:flex">
                                {headerNavItems.map((item) => {
                                    const isActive = activeHeaderMenuId === item.id;
                                    const isCurrentItem = isLandingHeaderNavItemCurrent(item, currentPath);

                                    if (item.kind === 'static') {
                                        // Captured in a const so the click closure below keeps
                                        // the narrowed string type instead of `item.href`'s optional one.
                                        const staticHref = item.href;
                                        if (staticHref) {
                                            return (
                                                <a
                                                    key={item.id}
                                                    href={staticHref}
                                                    onMouseEnter={() => handleHeaderNavEnter(item)}
                                                    onFocus={() => setActiveHeaderMenuId(null)}
                                                    onClick={() => {
                                                        trackNavClick('bar', 'header', item.id, staticHref);
                                                        closeHeaderMenu();
                                                    }}
                                                    aria-current={currentPath === staticHref ? 'page' : undefined}
                                                    class={[
                                                        HEADER_NAV_ITEM_BASE_CLASS,
                                                        isCurrentItem
                                                            ? HEADER_NAV_ITEM_ACTIVE_CLASS
                                                            : `text-slate-600 ${HEADER_NAV_ITEM_HOVER_CLASS}`,
                                                    ].join(' ')}
                                                >
                                                    <span>{item.label}</span>
                                                </a>
                                            );
                                        }

                                        return (
                                            <span
                                                key={item.id}
                                                class={[
                                                    HEADER_NAV_ITEM_BASE_CLASS,
                                                    'cursor-default text-slate-400',
                                                ].join(' ')}
                                            >
                                                <span>{item.label}</span>
                                            </span>
                                        );
                                    }

                                    return (
                                        <div
                                            key={item.id}
                                            class={[
                                                HEADER_MEGA_NAV_GROUP_BASE_CLASS,
                                                isActive || isCurrentItem
                                                    ? HEADER_NAV_ITEM_ACTIVE_CLASS
                                                    : `text-slate-600 ${HEADER_NAV_ITEM_HOVER_CLASS}`,
                                            ].join(' ')}
                                        >
                                            {item.href ? (
                                                <a
                                                    href={item.href}
                                                    class={HEADER_MEGA_NAV_LINK_CLASS}
                                                    onMouseEnter={() => handleHeaderNavEnter(item)}
                                                    onFocus={() => handleHeaderNavFocus(item)}
                                                    onKeyDown={(event) => handleHeaderMegaNavKeyDown(event, item)}
                                                    onPointerDown={(event) =>
                                                        handleHeaderMegaNavPointerDown(event, item)}
                                                    onPointerCancel={() => {
                                                        pendingPointerMenuIdRef.current = null;
                                                    }}
                                                    onClick={(event) => handleHeaderMegaNavClick(event, item)}
                                                    aria-expanded={isActive}
                                                    aria-controls={getHeaderMenuId(item.menu)}
                                                    data-testid={`header-${item.id}-link`}
                                                >
                                                    {item.label}
                                                </a>
                                            ) : (
                                                <button
                                                    type="button"
                                                    class={HEADER_MEGA_NAV_LINK_CLASS}
                                                    onMouseEnter={() => handleHeaderNavEnter(item)}
                                                    onFocus={() => handleHeaderNavFocus(item)}
                                                    onKeyDown={(event) => handleHeaderMegaNavKeyDown(event, item)}
                                                    onPointerDown={(event) =>
                                                        handleHeaderMegaNavPointerDown(event, item)}
                                                    onPointerCancel={() => {
                                                        pendingPointerMenuIdRef.current = null;
                                                    }}
                                                    onClick={() => handleHeaderMegaNavToggle(item)}
                                                    aria-expanded={isActive}
                                                    aria-controls={getHeaderMenuId(item.menu)}
                                                    data-testid={`header-${item.id}-link`}
                                                >
                                                    {item.label}
                                                </button>
                                            )}

                                            {item.menu === 'market' ? (
                                                <MarketMegaMenu
                                                    id={getHeaderMenuId('market')}
                                                    isOpen={activeHeaderMenu?.menu === 'market'}
                                                    news={navigation.news}
                                                    onNavigate={navigateFromMegaMenu('market')}
                                                />
                                            ) : null}

                                            {item.menu === 'tools' ? (
                                                <ToolsMegaMenu
                                                    id={getHeaderMenuId('tools')}
                                                    isOpen={activeHeaderMenu?.menu === 'tools'}
                                                    onNavigate={navigateFromMegaMenu('tools')}
                                                />
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </nav>

                            {domainsMenu ? (
                                <DomainsMegaMenu
                                    id={getHeaderMenuId('domains')}
                                    isOpen={activeHeaderMenu?.menu === 'domains'}
                                    menu={domainsMenu}
                                    navigation={navigation}
                                    screenerHref={links.screener}
                                    onNavigate={navigateFromMegaMenu('domains')}
                                />
                            ) : null}
                        </div>
                    </div>

                    <div class="ml-auto hidden items-center gap-3 lg:flex">
                        <CtaPill
                            href={isMember ? productHomeHref : links.productBaseUrl}
                            onClick={() => (isMember ? triggerOpenAppCta() : triggerPrimaryCta())}
                            variant="accent"
                            class="shadow-[0_14px_40px_rgba(27,29,30,0.16)]"
                        >
                            {isMember ? HEADER_OPEN_APP_LABEL : HEADER_PRIMARY_CTA_LABEL}
                        </CtaPill>
                    </div>

                    <button
                        ref={mobileMenuTriggerRef}
                        type="button"
                        class="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#e3e0d8] bg-white text-[#1b1d1e] shadow-[0_8px_20px_rgba(27,29,30,0.05)] lg:hidden"
                        onClick={() => setIsMobileMenuOpen(true)}
                        aria-expanded={isMobileMenuOpen}
                        aria-label="Open navigation menu"
                    >
                        <Icon name="menu" size={20} strokeWidth={2} />
                    </button>
                </div>
            </header>

            <MobileSiteMenu
                isOpen={isMobileMenuOpen}
                links={links}
                domainsMenu={domainsMenu}
                brandHref={brandHref}
                isMember={isMember}
                onBrandClick={handleBrandClick}
                onClose={closeMobileMenu}
                onNavigate={navigateFromMobileMenu}
                onLogin={() => triggerLoginCta()}
                onGithub={() => {
                    trackGithubNavOpen('header_mobile_menu');
                    closeMobileMenu();
                }}
            />

            {mobileActions === null ? null : (
                <MobileActionBar
                    secondary={mobileActions?.secondary === undefined ? defaultSecondaryAction : mobileActions.secondary}
                    primary={mobileActions?.primary || defaultPrimaryAction}
                />
            )}
        </>
    );
}
